import React from 'react';
import { motion } from 'framer-motion';
import { Star, MapPin } from 'lucide-react';
import { HERO } from '@/constants/testIds';
import Placeholder from './Placeholder';

const fade = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative min-h-screen pt-28 pb-20 overflow-hidden mandala-bg"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      {/* Ornamental SVG */}
      <svg className="absolute -top-32 -right-32 w-[640px] h-[640px] opacity-[0.10] text-maroon" viewBox="0 0 400 400" aria-hidden>
        <g stroke="currentColor" strokeWidth="0.6" fill="none" transform="translate(200 200)">
          {[...Array(24)].map((_, i) => (
            <g key={i} transform={`rotate(${(360 / 24) * i})`}>
              <path d="M0 0 C 40 -12, 80 -36, 120 0 C 80 36, 40 12, 0 0 Z" />
            </g>
          ))}
          <circle r="150" strokeDasharray="2 8" />
          <circle r="190" />
        </g>
      </svg>

      <div className="container-luxe relative grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fade} custom={0}>
            <span className="overline">Est. 2020 · Morrisville, NC</span>
          </motion.div>
          <motion.h1
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fade}
            custom={1}
            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] tracking-tight text-charcoal mt-5"
          >
            Discover the <em className="text-maroon not-italic font-medium">Beauty</em>
            <br className="hidden sm:block" /> of Indian Fashion
          </motion.h1>
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fade}
            custom={2}
            className="mt-7 text-base lg:text-lg text-charcoal/75 leading-relaxed max-w-xl"
          >
            Premium ethnic wear for weddings, festivals, celebrations, and every special occasion —
            curated and personally styled by Shally Chathey in the heart of North Carolina.
          </motion.p>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fade}
            custom={3}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#collections"
              data-testid={HERO.shopBtn}
              className="px-9 py-4 bg-burgundy hover:bg-maroon text-ivory text-xs uppercase tracking-[0.22em] transition-colors duration-500 text-center"
            >
              Shop Collection
            </a>
            <a
              href="#appointment"
              data-testid={HERO.bookBtn}
              className="px-9 py-4 border border-gold text-charcoal hover:bg-gold hover:text-ivory text-xs uppercase tracking-[0.22em] transition-all duration-500 text-center"
            >
              Book Appointment
            </a>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fade}
            custom={4}
            className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-5"
          >
            <div className="flex items-center gap-3">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                ))}
              </div>
              <a
                href="https://www.google.com/search?q=Trendy+Closet+Indian+Clothing+Reviews"
                target="_blank" rel="noreferrer"
                className="text-sm text-charcoal/80 hover:text-maroon transition-colors underline-offset-4 hover:underline"
              >
                <span className="font-semibold">5.0</span> · 40+ Google Reviews
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm text-charcoal/70">
              <MapPin className="w-4 h-4 text-maroon" /> Liberty Rose Dr · Morrisville, NC
            </div>
            <div className="flex items-center gap-3 text-xs text-charcoal/60">
              <span className="px-2.5 py-1 border border-brandborder rounded-full">Women Owned</span>
              <span className="px-2.5 py-1 border border-brandborder rounded-full">Asian Owned</span>
              <span className="px-2.5 py-1 border border-brandborder rounded-full">LGBTQ Friendly</span>
            </div>
          </motion.div>
        </div>

        {/* Right visual collage */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <div className="rounded-sm shadow-card overflow-hidden">
              <Placeholder
                label="Bridal Lehenga"
                tone="maroon"
                ratio="4/5"
                image="https://customer-assets.emergentagent.com/job_trendy-closet-luxury/artifacts/e1ol0cjd_WhatsApp%20Image%202026-06-27%20at%2015.22.43.jpeg"
                alt="Trendy Closet bridal pink lehenga with intricate silver embroidery"
              />
            </div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 5, ease: 'easeInOut' }}
              className="absolute -bottom-10 -left-8 w-40 md:w-52 shadow-card hidden sm:block"
            >
              <Placeholder label="Saree" tone="gold" ratio="3/4" />
            </motion.div>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 6, ease: 'easeInOut' }}
              className="absolute -top-8 -right-6 w-36 md:w-48 shadow-card hidden sm:block"
            >
              <Placeholder label="Anarkali" tone="blush" ratio="3/4" />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
